import axios from 'axios'
import { STORAGE_KEYS } from '../constants/storageKeys'
import { refreshTokenWithQueue } from '../utils/refreshToken'

// TODO LATER AXIOS INTERCEPTOR WITH QUEUE

// Setup axios
type SetupAxiosProps = (authCallbacks: any) => void

/**
 * Check if the current session is an impersonation session and get the token
 * @returns The impersonation token if valid, otherwise null
 */
const getImpersonationToken = (): string | null => {
  try {
    const impersonationData = sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_DATA)
    if (impersonationData) {
      const data = JSON.parse(impersonationData)
      // Check if token has expired
      const expiresAt = new Date(data.expiresAt)
      const now = new Date()
      if (now < expiresAt && data.isImpersonation) {
        return data.token
      }
    }
  } catch (error) {
    console.error('Error reading impersonation data:', error)
  }
  return null
}

/**
 * Decide how to react to a failed response: retry once via token refresh,
 * force logout if the retried request is still unauthorized, or leave the
 * error to be rejected as-is. Extracted for direct unit testing.
 */
export const handleUnauthorized = (error: any, authCallbacks: any) => {
  const tokenData = localStorage.getItem('token')
  const accessToken = tokenData && JSON.parse(tokenData)
  const originalRequest = error.config
  if (
    originalRequest &&
    error?.response?.status === 401 &&
    originalRequest.url !== '/auth/refresh' &&
    accessToken
  ) {
    if (!originalRequest._retry) {
      return refreshTokenWithQueue(originalRequest, authCallbacks)
    }
    // Retried request still unauthorized (any error body/shape) — session
    // is unrecoverable, force logout so PrivateRoute redirects to login
    // instead of rendering blank.
    authCallbacks.appLogOut()
  }
  return null
}

const setupAxiosInterceptors: SetupAxiosProps = (authCallbacks) => {
  axios.defaults.baseURL = process.env.REACT_APP_BASE_URL
  let loading = false
  let countRequest = 0

  // Middleware to valid requests. Add token to request
  const onRequestSuccess = (config: any) => {
    countRequest++
    if (!loading) loading = true
    
    // Admin endpoints that should ALWAYS use the admin token, not impersonation token
    // This allows admins to impersonate multiple candidates without issues
    const adminOnlyEndpoints = [
      '/v1/admin/impersonate',
      '/v1/impersonation'
    ]
    const isAdminEndpoint = adminOnlyEndpoints.some(endpoint => config.url?.includes(endpoint))
    
    // Check for impersonation token first (takes priority)
    // BUT: Don't use impersonation token for admin endpoints
    const impersonationToken = getImpersonationToken()
    if (impersonationToken && !isAdminEndpoint) {
      // config.headers.Authorization = `Bearer ${impersonationToken}`
      config.headers['X-Auth-Token'] = `Bearer ${impersonationToken}`
      return config
    }
    
    // Fall back to regular auth token
    const tokenData = localStorage.getItem('token')
    const accessToken = tokenData && JSON.parse(tokenData)
    if (accessToken) {
      // config.headers.Authorization = `Bearer ${accessToken.token}`
      config.headers['X-Auth-Token'] = `Bearer ${accessToken.token}`
    }
    return config
  }
  axios.interceptors.request.use(onRequestSuccess, void 0)

  const onResponseSuccess = (config: any) => {
    countRequest--
    if (!countRequest) {
      loading = false
    }
    return config
  }
  const onResponseFail = (error: any) => {
    countRequest--
    if (!countRequest) {
      loading = false
    }
    const handled = handleUnauthorized(error, authCallbacks)
    if (handled) {
      return handled
    }
    return Promise.reject(error)
  }
  axios.interceptors.response.use(onResponseSuccess, onResponseFail)
}

export default setupAxiosInterceptors
